import React from 'react'
import { useForm, useWatch } from 'react-hook-form'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button } from 'primereact/button'

import { RootState } from '../../redux/store'
import Text from '../../../ui/Text'
import { LandingPageLayout, SContentWrapper as SFormWrapper } from '../../../lib/landing-page-layout'
import { SFormHeader } from '../../../lib/form-header'
import { AlertIcon } from '../../../lib/icons/alert-icon'
import { notifyError } from '../../../common/features/notificationsManager/Notify'
import { isFetchBaseQueryError } from '../../../common/redux/api/fetchBaseQueryWithReauth'
import { type ChargePointSetOwnerApiArg, useChargePointSetOwnerMutation } from '../../../common/redux/api/web-api'
import { theme } from '../../../lib/theme'
import { useSignOut } from '../../../lib/sign-out'
import useText from '../../../common/hooks/useText'
import { MAX_NAME_LENGTH } from '../../../common/constants/InputsLength'
import { Input } from '../../../common/uikit/Input'

const ButtonGroup = styled.div`
    padding-top: 15px;
    display: flex;
    justify-content: center;
    gap: 10px;
`

const SSpacer = styled.div`
    margin-bottom: 49px;
`

const SContentWrapper = styled(SFormWrapper)`
    max-width: 510px;
    display: flex;
    flex-direction: column;
    align-items: center;
`

const SRegisterChargerInfo = styled.div`
    font-weight: var(--font-weight-light);
    font-size: 14px;
    color: ${({ theme }) => theme.font.midDarkColor};
    &,
    & > p {
        line-height: 22px;
    }
`

const SWarningMessage = styled.div`
    display: flex;
    justify-content: space-around;
    font-size: 11px;
    border: 1px solid #a1a1a1;
    border-radius: 6px;
    padding: 15px 11px;
    margin-bottom: 10px;
`

const SWarningMessageIcon = styled.span`
    margin-top: 2px;
    padding-right: 5px;
`

type FormData = Pick<ChargePointSetOwnerApiArg, 'chargePointId' | 'name' | 'wifiPassword'>

export function UserAppRegisterChargepoint(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const signOut = useSignOut()
    const userId = useSelector((state: RootState) => state.user.id)
    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isSubmitted, isValid },
    } = useForm<FormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            chargePointId: '',
            name: '',
            wifiPassword: '',
        },
    })
    const nameLength = useWatch({ control, name: 'name', defaultValue: '' }).trim().length
    const [addChargerToUser] = useChargePointSetOwnerMutation()

    const doAddChargertoUser = async (data: FormData) => {
        const name = data.name.trim()

        try {
            await addChargerToUser({
                userId: userId,
                chargePointId: data.chargePointId,
                name: name || data.chargePointId,
                wifiPassword: data.wifiPassword,
            }).unwrap()

            navigate('/dashboard', { replace: true })
        } catch (error: unknown) {
            if (
                isFetchBaseQueryError(error) &&
                (error.status === 409 || error.status === 404 || error.status === 403) &&
                typeof error.data === 'object' &&
                error.data !== null &&
                'error' in error.data &&
                typeof error.data.error === 'string'
            ) {
                notifyError(error.data.error)
            } else {
                notifyError('msgChargerNotAddedtoUser')
            }
        }
    }

    return (
        <LandingPageLayout background={theme.whiteBackground} hideLanguageSelect>
            <SContentWrapper>
                <SSpacer>
                    <SFormHeader>
                        <Text code="lblRegisterChargepointPage" />
                    </SFormHeader>
                </SSpacer>
                <SFormWrapper>
                    <form data-testid="create-modal-form" onSubmit={handleSubmit(doAddChargertoUser)} noValidate>
                        <SSpacer>
                            <Input
                                label={text('lblSerialNumber')}
                                {...register('chargePointId', {
                                    required: {
                                        value: true,
                                        message: text('msgSerialNumberRequired'),
                                    },
                                })}
                                id="serialNumber"
                                error={errors.chargePointId?.message}
                                data-testid="serialNumber"
                                required
                            />

                            <div className="margin-top-med ">
                                <Input
                                    label={text('lblPin')}
                                    id="charger-pin"
                                    {...register('wifiPassword', {
                                        required: {
                                            value: true,
                                            message: text('msgPinCodeRequired'),
                                        },
                                        maxLength: {
                                            value: 8,
                                            message: text('msgPinCodeLength'),
                                        },
                                    })}
                                    error={errors?.wifiPassword?.message}
                                    data-testid="pinCode"
                                    required
                                />
                            </div>

                            <div className="margin-top-med ">
                                <Input
                                    id="name"
                                    label={text('lblChargersNameField')}
                                    {...register('name', {
                                        validate: (value) =>
                                            value.trim().length <= MAX_NAME_LENGTH || text('lblNameError'),
                                    })}
                                    error={errors.name?.message}
                                    subtitle={
                                        <span className="flex justify-content-end">
                                            {text('msgTextAreaCounter', {
                                                textCount: nameLength,
                                                maxLength: MAX_NAME_LENGTH,
                                            })}
                                        </span>
                                    }
                                    data-testid="name"
                                />
                            </div>
                        </SSpacer>
                        <SSpacer>
                            <SWarningMessage data-testid="warningtext">
                                <SWarningMessageIcon>
                                    <AlertIcon size={20} fill={theme.brandColors.CA_Blue} />
                                </SWarningMessageIcon>
                                <Text code="lblRegisterChargepointPageWarning" />
                            </SWarningMessage>

                            <SRegisterChargerInfo>
                                <Text code="msgRegisterChargerInfo" />
                            </SRegisterChargerInfo>
                        </SSpacer>

                        <ButtonGroup>
                            <Button
                                type="button"
                                label={text('btnCancel')}
                                outlined
                                onClick={signOut}
                                data-testid="cancelBtn"
                            />
                            <Button
                                label={text('btnNext')}
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                type="submit"
                                data-testid="goBtn"
                                disabled={isSubmitted && !isValid}
                            />
                        </ButtonGroup>
                    </form>
                </SFormWrapper>
            </SContentWrapper>
        </LandingPageLayout>
    )
}
